import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { HashRouter, Routes, Route, useLocation } from "react-router-dom";
import { AnimatePresence } from "framer-motion";
import { ROUTE_PATHS } from "@/lib/index";
import { AuthProvider, useAuth } from "@/hooks/useAuth";
import AuthModal        from "@/components/AuthModal";
import UserMenu         from "@/components/UserMenu";
import { useUserSync } from "@/hooks/useUserSync";
import HomePage         from "./pages/Home";
import StoresPage       from "./pages/Stores";
import ScanningPage     from "./pages/Scanning";
import MealPlanPage     from "./pages/MealPlan";
import RecipePage       from "./pages/Recipe";
import GroceryList      from "./pages/GroceryList";
import SavedLists       from "./pages/SavedLists";
import ReviewsPage      from "./pages/Reviews";
import WeeklyCalendar   from "./pages/WeeklyCalendar";
import AdminPage        from "./pages/Admin";
import MyPantryPage     from "./pages/MyPantry";
import FlyerDealsPage   from "./pages/FlyerDeals";
import SavedRecipesPage from "./pages/SavedRecipes";
import NotFound         from "./pages/not-found/Index";
import FeedbackButton   from "@/components/FeedbackButton";
import AppHeader        from "@/components/AppHeader";
import BottomNav        from "@/components/BottomNav";

const queryClient = new QueryClient();

function GlobalAuth() {
  const { showAuthModal } = useAuth();
  useUserSync(); // ← sync all user data to/from Supabase
  return (
    <AnimatePresence>
      {showAuthModal && <AuthModal />}
    </AnimatePresence>
  );
}

// Floating avatar/sign-in — hidden on Home (Home has its own nav)
function FloatingUserMenu() {
  const { loading } = useAuth();
  const { hash }    = useLocation();
  // Home = hash is '' or '#/' or '#'
  if (loading || hash === '' || hash === '#/' || hash === '#' || !hash) return null;
  return (
    <div className="fixed top-3 right-4 z-30">
      <UserMenu />
    </div>
  );
}

const App = () => (
  <QueryClientProvider client={queryClient}>
    <AuthProvider>
      <TooltipProvider>
        <Toaster />
        <Sonner />
        <HashRouter>
          <GlobalAuth />
          <AppHeader />
          <BottomNav />
          <FeedbackButton />
          <Routes>
            <Route path={ROUTE_PATHS.HOME}        element={<HomePage />} />
            <Route path={ROUTE_PATHS.STORES}      element={<StoresPage />} />
            <Route path={ROUTE_PATHS.LOADING}     element={<ScanningPage />} />
            <Route path={ROUTE_PATHS.MEAL_PLAN}   element={<MealPlanPage />} />
            <Route path={ROUTE_PATHS.RECIPE}      element={<RecipePage />} />
            <Route path={ROUTE_PATHS.GROCERY}     element={<GroceryList />} />
            <Route path={ROUTE_PATHS.SAVED_LISTS} element={<SavedLists />} />
            <Route path={ROUTE_PATHS.REVIEWS}     element={<ReviewsPage />} />
            <Route path={ROUTE_PATHS.CALENDAR}    element={<WeeklyCalendar />} />
            <Route path={ROUTE_PATHS.ADMIN}       element={<AdminPage />} />
            <Route path={ROUTE_PATHS.MY_PANTRY}   element={<MyPantryPage />} />
            <Route path={ROUTE_PATHS.FLYER}          element={<FlyerDealsPage />} />
            <Route path={ROUTE_PATHS.SAVED_RECIPES}  element={<SavedRecipesPage />} />
            <Route path="*"                       element={<NotFound />} />
          </Routes>
        </HashRouter>
      </TooltipProvider>
    </AuthProvider>
  </QueryClientProvider>
);

export default App;
